import { Box, Container, Typography, Link } from '@mui/material'

import Head from '@/components/head/Head'
import BravoLogo from '@/assets/images/Bravo_Hero_RGB_Colour.png'

export default function HomePage() {
  return (
    <>
      <Head
        title="NBCU International Networks"
        description="Official website for NBC Universal International Networks España S.L.U. Legal information for Bravo EMEA, E! International and other NBCU network brands. Contact information for Bravo France, Bravo Africa, and E! EURA International."
        keywords="NBC Universal, NBCU International Networks, Bravo EMEA, Bravo International, E! International, NBCUniversal España, Bravo France, Bravo Africa, E! EURA, legal information, contact information"
        canonicalUrl="https://www.nbcuintnetworks.com/"
      />
      <Box
        sx={{
          minHeight: 'calc(100vh - 200px)',
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            {/* Bravo Logo */}
            <Box
              sx={{
                mb: 5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={BravoLogo}
                alt="Bravo International Logo"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '180px', sm: '240px', md: '280px' },
                  height: 'auto',
                }}
              />
            </Box>

            {/* Main Heading */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: 5,
                fontWeight: 600,
                color: '#fff',
                fontSize: { xs: '1.75rem', sm: '2rem', md: '3rem' },
                letterSpacing: '-0.01em',
                fontFamily:
                  '"Rock Sans", "Trebuchet MS", "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Tahoma, sans-serif',
                textTransform: 'none',
              }}
            >
              Bravo EMEA
            </Typography>

            {/* Company Info */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1.5,
                  color: '#fff',
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  lineHeight: 1.7,
                }}
              >
                NBC Universal Global Networks España S.L.U.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 1.5,
                  color: '#b0b0b0',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.7,
                }}
              >
                Torre Europa, Paseo de la Castellana 95, Planta 10
                <br />
                28046 Madrid, SPAIN
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#b0b0b0',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.7,
                }}
              >
                RCS: B-82227893
              </Typography>
            </Box>

            {/* Ownership Info with Gradient Border */}
            <Box
              sx={{
                py: 3,
                mb: 4,
                px: 4,
                borderRadius: '8px',
                background:
                  'linear-gradient(135deg, rgba(67, 3, 151, 0.1) 0%, rgba(254, 47, 97, 0.1) 100%)',
                border: '1px solid',
                borderImage: 'linear-gradient(135deg, #430397, #fe2f61) 1',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#d0d0d0',
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  lineHeight: 1.7,
                  fontStyle: 'italic',
                }}
              >
                NBC Universal Global Networks España S.L.U. is wholly owned by
                Universal Studios International BV.
              </Typography>
            </Box>

            {/* Regulatory Information */}
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#fff',
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  lineHeight: 1.8,
                  mb: 2.5,
                  fontWeight: 600,
                }}
              >
                The following channels are subject to Spanish jurisdiction and
                regulated by the National Commission on Competition & Markets
                (CNMC):
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#b0b0b0',
                    fontSize: { xs: '0.85rem', md: '0.9rem' },
                    lineHeight: 1.7,
                  }}
                >
                  <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>
                    Bravo France
                  </Box>{' '}
                  - Andorra, Belgium, DROM‑COM (French Overseas Departments and
                  Collectivities), France, Luxembourg, Monaco and Switzerland.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#b0b0b0',
                    fontSize: { xs: '0.85rem', md: '0.9rem' },
                    lineHeight: 1.7,
                  }}
                >
                  <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>
                    E! EURA
                  </Box>{' '}
                  - Belgium, Bosnia and Herzegovina, Bulgaria, Croatia, Cyprus,
                  Czech Republic, Denmark, Estonia, Finland, Gibraltar, Greece,
                  Hungary, Iceland, Latvia, Lithuania, Luxembourg, Malta,
                  Montenegro, the Netherlands, North Macedonia, Norway, Poland,
                  Portugal, Romania, Serbia, Slovakia, Slovenia and Sweden.
                </Typography>
              </Box>
            </Box>

            {/* Gradient Divider */}
            <Box
              sx={{
                height: '2px',
                background:
                  'linear-gradient(90deg, transparent 0%, #430397 20%, #fe2f61 80%, transparent 100%)',
                mb: 5,
              }}
            />

            {/* Contact Section */}
            <Box sx={{ pt: 3 }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  mb: 4,
                  fontWeight: 600,
                  color: '#fff',
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  letterSpacing: '-0.01em',
                }}
              >
                Contact Us
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#fff',
                      mb: 0.75,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.05rem' },
                    }}
                  >
                    Bravo France
                  </Typography>
                  <Link
                    href="mailto:communication_fr@nbcuni.com"
                    sx={{
                      color: '#fe2f61',
                      textDecoration: 'none',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#430397',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    communication_fr@nbcuni.com
                  </Link>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#fff',
                      mb: 0.75,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.05rem' },
                    }}
                  >
                    Bravo Africa
                  </Typography>
                  <Link
                    href="mailto:nbcu.intnetworks@nbcuni.com"
                    sx={{
                      color: '#fe2f61',
                      textDecoration: 'none',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#430397',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    nbcu.intnetworks@nbcuni.com
                  </Link>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#fff',
                      mb: 0.75,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.05rem' },
                    }}
                  >
                    E! EURA International
                  </Typography>
                  <Link
                    href="mailto:Contact.EChannel_Int@nbcuni.com"
                    sx={{
                      color: '#fe2f61',
                      textDecoration: 'none',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#430397',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Contact.EChannel_Int@nbcuni.com
                  </Link>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  )
}
